// brandService.ts - Manages authenticated user's brand

import { getCurrentUser } from './auth';
import { brands } from './config/brands/index';

/**
 * Returns the brand of the currently logged in user.
 * Throws error if no user is logged in or user has no brand assigned.
 */
export function getCurrentBrand(): string {
  const user = getCurrentUser();

  if (!user) {
    throw new Error('No user logged in. Cannot determine brand.');
  }

  if (!user.brand) {
    throw new Error(`User ${user.username} has no brand assigned. Please contact administrator.`);
  }

  return user.brand;
}

/**
 * Checks if the user has access to a specific brand.
 * Supports wildcard "*" for all brands and explicit brand lists.
 * Falls back to primary brand check for users without a brands list.
 */
export function hasAccessToBrand(brandId: string): boolean {
  const user = getCurrentUser();
  if (!user) return false;

  if (!user.brands || user.brands.length === 0) {
    return user.brand === brandId;
  }

  if (user.brands.includes('*')) return true;

  return user.brands.includes(brandId);
}

/**
 * Returns the list of brand IDs the current user can access.
 * Wildcard "*" expands to all registered brand IDs.
 */
export function getAccessibleBrands(): string[] {
  const user = getCurrentUser();
  if (!user) return [];

  if (!user.brands || user.brands.length === 0) {
    return user.brand ? [user.brand] : [];
  }

  if (user.brands.includes('*')) {
    return Object.keys(brands);
  }

  return user.brands;
}

/**
 * Returns current user's brand information.
 * Throws error if no user is logged in.
 */
export function getCurrentBrandInfo() {
  const brandId = getCurrentBrand();
  const user = getCurrentUser();

  if (!user) {
    throw new Error('No user logged in. Cannot get brand information.');
  }

  return {
    brandId,
    username: user.username,
    viewer: user.viewer
  };
}
